import React, { useEffect, useState } from "react";
import { Cookie, Shield, X, Check, Settings2 } from "lucide-react";
import { Link } from "react-router-dom";

// Le module de notifications n'est chargé qu'au premier message affiché.
const notify = (title, description, duration) =>
  import("sonner").then((m) => m.toast.success(title, { description, duration }));

const STORAGE_KEY = "vp-cookie-consent";

export function getConsent() {
  if (typeof window === "undefined") return null;
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) || "null");
  } catch {
    return null;
  }
}

export function saveConsent(consent) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...consent, ts: Date.now() }));
  window.dispatchEvent(new Event("vp-consent-updated"));
}

export default function CookieBanner() {
  const [open, setOpen] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [matterport, setMatterport] = useState(true);
  const [analytics, setAnalytics] = useState(false);

  useEffect(() => {
    const existing = getConsent();
    if (!existing) setOpen(true);
  }, []);

  const acceptAll = () => {
    saveConsent({ essential: true, matterport: true, analytics: true });
    setOpen(false);
    notify("Cookies activés", "Vos préférences ont bien été enregistrées.", 3500);
  };
  const refuseAll = () => {
    saveConsent({ essential: true, matterport: false, analytics: false });
    setOpen(false);
    notify("Choix enregistré", "Seuls les cookies essentiels sont actifs.", 3500);
  };
  const saveChoice = () => {
    saveConsent({ essential: true, matterport, analytics });
    setOpen(false);
    setShowSettings(false);
    notify("Préférences enregistrées", "Vous pouvez les modifier à tout moment depuis le pied de page.", 4000);
  };

  if (!open) return null;

  return (
    <>
      <div
        className="fixed inset-0 bg-black/40 backdrop-blur-sm z-[99] pointer-events-none vp-fade-in"
      />
      <div
        role="dialog"
        aria-label="Consentement aux cookies"
        data-testid="cookie-banner"
        className="fixed bottom-3 left-3 right-3 md:left-6 md:right-6 md:bottom-6 z-[100] max-w-5xl md:mx-auto vp-slide-up"
      >
            <div className="surface-card rounded-2xl border-2 border-[#E50914]/30 shadow-2xl overflow-hidden max-h-[85dvh] overflow-y-auto">
              {!showSettings ? (
                <div className="p-4 md:p-7 flex flex-col md:flex-row items-start gap-5">
                  <div className="hidden md:flex w-12 h-12 rounded-full bg-[#E50914]/15 border border-[#E50914]/40 items-center justify-center flex-shrink-0">
                    <Cookie className="w-5 h-5 text-[#E50914]" />
                  </div>
                  <div className="flex-grow min-w-0">
                    <div className="flex items-center gap-2 mb-1.5">
                      <Cookie className="md:hidden w-4 h-4 text-[#E50914]" />
                      <h3 className="font-display text-base md:text-lg font-medium">Vos cookies, votre choix</h3>
                    </div>
                    <p className="text-xs md:text-sm text-zinc-400 leading-relaxed">
                      <span className="md:hidden">
                        Cookies essentiels uniquement ; mesure d'audience et visites Matterport sur accord.
                      </span>
                      <span className="hidden md:inline">
                        VirtualPeek® utilise des cookies techniques essentiels au bon fonctionnement du
                        site. La mesure d'audience (statistiques de fréquentation) et les visites Matterport
                        ne déposent des traceurs qu'avec votre accord. Vous décidez.
                      </span>
                      {" "}
                      <Link to="/politique-confidentialite" className="text-[#E50914] hover:underline inline-flex items-center min-h-[36px] md:min-h-0">
                        En savoir plus
                      </Link>
                    </p>
                    <div className="grid grid-cols-2 gap-2 mt-3 md:mt-5 md:flex md:flex-wrap md:items-center">
                      <button
                        onClick={acceptAll}
                        data-testid="cookie-accept"
                        className="btn-primary text-sm justify-center whitespace-nowrap !px-3 md:!px-6"
                      >
                        <Check className="w-4 h-4" /> Tout accepter
                      </button>
                      <button
                        onClick={refuseAll}
                        data-testid="cookie-refuse"
                        className="btn-secondary text-sm justify-center whitespace-nowrap !px-3 md:!px-6"
                      >
                        <X className="w-4 h-4" /> Tout refuser
                      </button>
                      <button
                        onClick={() => setShowSettings(true)}
                        data-testid="cookie-settings"
                        className="col-span-2 inline-flex items-center justify-center gap-1.5 min-h-[44px] text-xs text-zinc-400 hover:text-white underline underline-offset-4 transition-colors md:ml-2"
                      >
                        <Settings2 className="w-3.5 h-3.5" /> Personnaliser
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="p-4 md:p-7" data-testid="cookie-settings-panel">
                  <div className="flex items-center gap-3 mb-6">
                    <Shield className="w-5 h-5 text-[#E50914]" />
                    <h3 className="font-display text-lg font-medium">Préférences de confidentialité</h3>
                  </div>

                  <div className="space-y-3">
                    <Row
                      title="Essentiels"
                      caption="Fonctionnement du site, préférences de thème, session admin. Toujours actifs."
                      locked
                      checked
                    />
                    <Row
                      title="Visites virtuelles Matterport"
                      caption="Le lecteur Matterport dépose des cookies techniques et collecte des données de navigation à l'activation d'une visite virtuelle."
                      checked={matterport}
                      onChange={setMatterport}
                      testId="toggle-matterport"
                    />
                    <Row
                      title="Mesure d'audience"
                      caption="Statistiques de fréquentation anonymisées (pages vues et clics), chargées uniquement avec votre accord et après l'affichage du site. Aucun enregistrement de session, aucun sondage."
                      checked={analytics}
                      onChange={setAnalytics}
                      testId="toggle-analytics"
                    />
                  </div>

                  <div className="flex flex-wrap gap-2 mt-6">
                    <button onClick={saveChoice} data-testid="cookie-save" className="btn-primary text-sm">
                      <Check className="w-4 h-4" /> Enregistrer mes choix
                    </button>
                    <button onClick={() => setShowSettings(false)} className="btn-secondary text-sm">
                      Retour
                    </button>
                  </div>
                </div>
              )}
            </div>
      </div>
    </>
  );
}

function Row({ title, caption, checked, onChange, locked, testId }) {
  return (
    <div className="flex items-start justify-between gap-4 p-4 rounded-lg bg-white/[0.02] border border-white/5">
      <div>
        <div className="text-sm font-medium text-white">{title}</div>
        <div className="text-xs text-zinc-400 mt-1 leading-relaxed">{caption}</div>
      </div>
      <label className={`relative inline-flex flex-shrink-0 items-center ${locked ? "opacity-60" : "cursor-pointer"}`}>
        <input
          type="checkbox"
          checked={!!checked}
          disabled={locked}
          onChange={(e) => !locked && onChange?.(e.target.checked)}
          data-testid={testId}
          className="sr-only peer"
        />
        <span className="w-11 h-6 bg-white/10 border border-white/10 rounded-full peer-checked:bg-[#E50914] peer-checked:border-[#E50914] transition" />
        <span className="absolute left-0.5 top-0.5 w-5 h-5 bg-white rounded-full transition peer-checked:translate-x-5" />
      </label>
    </div>
  );
}
